import { tracking } from '@iwoca/frontend-tracking-library';
import { IconAngleLeft, IconShieldCheck } from '@iwoca/icons';
import { AlertBox, Button, IwocaLogo, Link } from '@iwoca/orion';

import styles from './BankSelectionStep.module.css';
import { useGetUserConsent } from '../../../../../api/lendingApi/lendingApiQueries';
import { useUserConsentMutation } from '../../../../../api/userConsentApi/useUserConsentMutation';
import { useContextStateKey } from '../../../../../hooks/useContextStateKey';
import { getAmplitudeDefaultEventProperties } from '../../../../../utils/amplitudeEvents';
import logger from '../../../../../utils/logger';
import BankLinksSVG from '../../../OpenBanking/BankLinkSVG/BankLinkSVG';
import { TOnlineBank } from '../../../OpenBanking/OpenBanking.types';

type TProps = {
  onClickBack: () => void;
  selectedBank: TOnlineBank;
  bankUrlError: boolean;
  bankAuthURL: string;
};

const TRUE_LAYER_OPEN_BANKING_PROVIDER = 'truelayer_open_banking';
// Note: The original BankConnectionView had DE-specific logic (shouldSkipPermissionView)
// that auto-redirected to the bank. This UK-only component always shows the permission view.
const BankConnectionStep = ({
  onClickBack,
  selectedBank,
  bankUrlError,
  bankAuthURL,
}: TProps) => {
  const stateKey = useContextStateKey();
  const { userConsent } = useGetUserConsent();
  const { postUserConsent } = useUserConsentMutation();

  const handleBankConnectionCTA = () => {
    tracking.ampli.trueLayerBankConnectStarted({
      ...getAmplitudeDefaultEventProperties(),
      bankId: selectedBank?.bank_id as string,
      bankName: selectedBank?.bank_name,
    });

    if (!userConsent) {
      postUserConsent({
        body: {
          data: {
            bundle_hash: viteBuildHash,
          },
        },
        provider: TRUE_LAYER_OPEN_BANKING_PROVIDER,
        stateKey: stateKey as string,
      }).catch((error) => {
        logger.error('Failed to store user consent', { error });
      });
    }
    window.location.href = bankAuthURL;
  };

  const selectedBankName = selectedBank.bank_name;

  return (
    <>
      <div className={styles.bankConnectionContainer}>
        <div className={styles.bankConnectionHeader}>
          <Button
            variant="tertiary"
            onClick={onClickBack}
            className={styles.backButton}
          >
            <IconAngleLeft />
            Back
          </Button>
          <div className={styles.bankConnectionLogos}>
            <IwocaLogo iconOnly className={styles.iwocaLogo} />
            <BankLinksSVG classNames={styles.bankLink} />
            <figure className={styles.bankConnectionLogo}>
              <img
                src={selectedBank?.logo_url}
                alt={selectedBank?.bank_name}
                className={styles.bankLogo}
              />
            </figure>
          </div>
        </div>
        <section className={styles.bankConsentContainer}>
          <h3 className={styles.descriptionHeader}>What is being shared?</h3>
          <p>
            To assess your ability to repay a loan, TrueLayer needs permission
            to access the following information and share it with Iwoca:
          </p>
          <ul>
            <li>Full name</li>
            <li>Account number and sort code</li>
            <li>Balance</li>
            <li>Transactions, direct debits, and standing orders.</li>
          </ul>
          <div className={styles.bankConnectionGradeContainer}>
            <div className={styles.bankGradeContainer}>
              <IconShieldCheck className={styles.bankGradeIcon} />
              <p>Bank-grade encryption</p>
            </div>
            <p className={styles.bankGradeDescription}>
              We use a partner called TrueLayer, to access the information
              needed to assess your ability to repay a loan.
            </p>
          </div>
        </section>
        {bankUrlError && selectedBank && (
          <AlertBox variant="failure">
            <p className={styles.bankUrlError}>
              Sorry, we ran into an issue while retrieving the link for{' '}
              {selectedBankName}. Please try again later.
            </p>
          </AlertBox>
        )}
        <div className={styles.ctaContainer}>
          <Button
            data-testid="truelayer-bank-url"
            onClick={handleBankConnectionCTA}
            rel="noopener noreferrer"
            loading={!bankAuthURL && !bankUrlError}
            disabled={Boolean(bankUrlError)}
            aria-disabled={bankUrlError}
            size="large"
          >
            Continue to bank login
          </Button>
        </div>
        <div
          className={styles.trueLayerFooter}
          data-testid="true-layer-modal-footer"
        >
          <p>
            By clicking or tapping ‘Agree and generate shareable link’, you
            agree to{' '}
            <Link
              data-testid="terms-of-service-link"
              href="https://truelayer.com/legal/enduser_tos/"
              target="_blank"
              rel="noopener noreferrer"
            >
              TrueLayer’s Terms of Service
            </Link>{' '}
            and{' '}
            <Link
              data-testid="privacy-policy-link"
              href="https://truelayer.com/legal/privacy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>
    </>
  );
};

export default BankConnectionStep;
